/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { observer } from 'mobx-react';
import * as React from 'react';
import { IFarmlandsStoreSelectorData } from './farmlands-store-selector.data';
import { IFarmlandsStoreSelectorProps } from './farmlands-store-selector.props.autogenerated';

import {
    StoreSelectorEmptyMessage,
    StoreSelectorFoundLocationsMessage,
    StoreSelectorLocationLines,
    StoreSelectorSearchForm,
    StoreSelectorTermsOfService,
    StoreSelectorWaiting
} from './components';

import { getFullAvailableInventoryNearby, GetFullAvailableInventoryNearbyInput } from '@msdyn365-commerce-modules/retail-actions';

import { IModuleProps, INodeProps, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';

export interface IStoreSelectorState {
    isSearchInProgress: boolean;
    searchTerm?: string;
    selectedStore?: OrgUnitLocation;
    hasSearched?: boolean;
    hasSubmitted?: boolean;
}

export interface IStoreSelectorViewProps extends IFarmlandsStoreSelectorProps<IFarmlandsStoreSelectorData> {
    state: IStoreSelectorState;

    // Containers:
    ModuleProps: IModuleProps;
    BodyContainerProps: INodeProps;
    FooterContainerProps: INodeProps;
    HeaderContainerProps: INodeProps;
    StoreSelectorContainerProps: INodeProps;

    // Components
    locationsMessage?: React.ReactNode;
    search?: React.ReactNode;
    spinner?: React.ReactNode;
    locationsList?: React.ReactNode;
    noLocationsMessage?: React.ReactNode;
    terms?: React.ReactNode;
    headerText?: React.ReactNode;
    cancelButton?: React.ReactNode;
    submitButton?: React.ReactNode;

    callbacks: {
        closeDialog(): Promise<void>;
        searchTermChanged(searchTerm: string): Promise<void>;
        performSearch(searchTerm: string): Promise<void>;
        selectedLocation(location: OrgUnitLocation | undefined): void;
        getAndUpdateUserLocation(): Promise<void>;
    };
}

/**
 *
 * Pick Up In Store module
 * @extends React.Component<IPickUpInStoreProps<IPickUpInStoreData>>}
 */
@observer
class StoreSelector extends React.Component<IFarmlandsStoreSelectorProps<IFarmlandsStoreSelectorData>, IStoreSelectorState> {
    constructor(props: IFarmlandsStoreSelectorProps<IFarmlandsStoreSelectorData>) {
        super(props);

        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        this.state = {
            isSearchInProgress: false,
            searchTerm: '',
            selectedStore: storeSelectorStateManager?.context?.alreadySelectedLocation,
            hasSearched: false,
            hasSubmitted: false
        };
    }

    public render(): JSX.Element {
        const {
            config: moduleConfig,
            context: {
                telemetry,
                app: { config: appConfig },
                actionContext: {
                    requestContext: { channel }
                }
            },
            resources,
            data: {
                availabilitiesWithHours: { result: availabilitiesWithHours },
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const isBingMapsDisabled = channel && channel.BingMapsApiKey && channel.BingMapsEnabled ? false : true;
        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        const isDialogOpen: boolean = storeSelectorStateManager ? storeSelectorStateManager.isDialogOpen : false;

        if (!(channel && channel.BingMapsApiKey)) {
            telemetry.error('BingMapsApiKey is missing.');
        }

        const viewProps = {
            ...(this.props as IFarmlandsStoreSelectorProps<IFarmlandsStoreSelectorData>),
            state: this.state,
            StoreSelectorContainerProps: {
                tag: 'div',
                className: 'ms-store-select__modal-container'
            },
            ModuleProps: {
                tag: Modal,
                moduleProps: this.props,
                className: classname('ms-store-select', moduleConfig.className),
                autoFocus: true,
                fade: true,
                isOpen: isDialogOpen,
                onClosed: this._closeDialog,
                toggle: this._closeDialog,
                horizontalPosition: 'center',
                verticalPosition: 'center'
            },
            HeaderContainerProps: {
                tag: ModalHeader,
                className: 'ms-store-select__modal-header'
            },
            FooterContainerProps: {
                tag: ModalFooter,
                className: 'ms-store-select__modal-footer'
            },
            BodyContainerProps: {
                tag: ModalBody,
                className: 'ms-store-select__modal-body'
            },
            locationsMessage: (
                <StoreSelectorFoundLocationsMessage
                    resources={resources}
                    locations={availabilitiesWithHours}
                    searchRadius={moduleConfig.lookupRadius || 50}
                />
            ),
            search: (
                <StoreSelectorSearchForm
                    value={this.state.searchTerm || ''}
                    resources={resources}
                    performSearch={this._performSearch}
                    searchTermChanged={this._searchTermChanged}
                    isBingMapsDisabled={isBingMapsDisabled}
                />
            ),
            spinner: <StoreSelectorWaiting />,
            locationsList: availabilitiesWithHours && availabilitiesWithHours.length > 0 && (
                <StoreSelectorLocationLines
                    locations={availabilitiesWithHours}
                    resources={resources}
                    alreadySelectedLocation={context ? context.alreadySelectedLocation : undefined}
                    outOfStockThreshold={appConfig.outOfStockThreshold || 0}
                    enableOutOfStockCheck={appConfig.enableStockCheck || false}
                    onLocationSelected={this._onLocationItemSelected}
                />
            ),
            noLocationsMessage: <StoreSelectorEmptyMessage resources={resources} />,
            terms: <StoreSelectorTermsOfService link={moduleConfig.termsOfServiceLink} />,
            headerText: <span>{resources.storeSelectorHeaderText}</span>,
            cancelButton: (
                <button className='ms-store-select__cancel' onClick={this._closeDialog}>
                    Cancel
                </button>
            ),
            submitButton: (
                <button
                    className='ms-store-select__submit'
                    disabled={this.state.selectedStore === undefined}
                    onClick={this._onLocationSubmit}
                >
                    {this.state.hasSubmitted && <div className='msc-waiting-circular ms-store-select__submit-spinner' />}
                    <span>Select Store</span>
                </button>
            ),

            callbacks: {
                searchTermChanged: this._searchTermChanged,
                performSearch: this._performSearch,
                closeDialog: this._closeDialog,
                selectedLocation: this._onLocationItemSelected,
                getAndUpdateUserLocation: () => {
                    this._getAndUpdateUserLocation();
                    return Promise.resolve();
                }
            }
        } as IStoreSelectorViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _performSearch = async (searchTerm: string): Promise<void> => {
        const {
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!searchTerm || !storeSelectorStateManager) {
            return;
        }

        if(this.state.selectedStore){
            this.setState({ selectedStore: undefined });
        }

        this.setState({ isSearchInProgress: true, hasSearched: true });
        searchTerm = `${searchTerm} `;
        const location = await storeSelectorStateManager.geoLocate(searchTerm.trim() !== '' ? searchTerm : '0', actionContext);

        if (location) {
            await this._updateLocations(location.latitude, location.longitude);
        }

        this.setState({ isSearchInProgress: false });
    };

    private _searchTermChanged = async (searchTerm: string): Promise<void> => {
        this.setState({ searchTerm: searchTerm });
    };

    private _getAndUpdateUserLocation = (): void => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager || !storeSelectorStateManager.isDialogOpen) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }

        this.setState({ isSearchInProgress: true });

        if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    await this._updateLocations(position.coords.latitude, position.coords.longitude);

                    this.setState({ isSearchInProgress: false });
                },
                async (error) => {
                    this.setState({ isSearchInProgress: false });
                },
                { timeout: 2000 }
            );
        } else {
            this.setState({ isSearchInProgress: false });
        }
    };

    private _closeDialog = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        this.setState({ searchTerm: '', selectedStore: undefined, hasSearched: false });

        if (storeSelectorStateManager) {
            return storeSelectorStateManager.closeDialog();
        }
    };

    private _updateLocations = async (latitude: number | undefined, longitude: number | undefined): Promise<void> => {
        const {
            telemetry,
            config: { lookupRadius },
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager || !storeSelectorStateManager.context) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }

        if (!storeSelectorStateManager.context.product) {
            // TODO: Use alternative search method here to not make inventory check
            return;
        }

        const input = new GetFullAvailableInventoryNearbyInput(
            storeSelectorStateManager.context.product.RecordId,
            latitude || 0,
            longitude || 0,
            lookupRadius || 50
        );

        await getFullAvailableInventoryNearby(input, actionContext).catch((error) => {
            telemetry.warning(error);
            telemetry.error(error.message);
            telemetry.debug(`Unable to get full available inventory near by`);
        });
    };

    private _onLocationItemSelected = (location: OrgUnitLocation | undefined): void => {
        this.setState({ selectedStore: location });
    };

    private _onLocationSubmit = async (): Promise<void> => {
        const {
            telemetry,
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager) {
            if (this.state.selectedStore && storeSelectorStateManager.context) {
                this.setState({
                    hasSubmitted: true
                });
                // Want to eat exceptions here so that the dialog still closes even if the onLocationSelected call goes wrong
                await storeSelectorStateManager.context.onLocationSelected(this.state.selectedStore, 'pickupInStore').catch((error) => {
                    telemetry.warning(error);
                    telemetry.error(error.message);
                    telemetry.debug(`Unable to get on org unit location selected`);
                });
            }

            this.setState({
                selectedStore: undefined,
                hasSubmitted: false
            });

            await storeSelectorStateManager.closeDialog();
        }
    };
}

export default StoreSelector;
